// ============================================================================
// 幾何ユーティリティ / Geometry helpers
// すべて [lng, lat] 順（MapLibre 準拠）。距離は小範囲の等距円筒近似で十分。
// 出典: /Users/ntuf/Workspace/ShadowStep の src/lib/geo.ts を移植・トリム。
// ============================================================================

import type { LngLat } from "./types";

export const EARTH_R = 6378137; // 地球半径 (m, WGS84長半径)
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

/** 緯度における「経度1度あたりのメートル数」。 */
export function metersPerDegLng(lat: number): number {
  return (Math.PI / 180) * EARTH_R * Math.cos(lat * DEG2RAD);
}
export const METERS_PER_DEG_LAT = (Math.PI / 180) * EARTH_R;

/** 2点間の距離 (m)。等距円筒近似（数十kmまで誤差ごくわずか）。 */
export function distanceMeters(a: LngLat, b: LngLat): number {
  const latMid = (a[1] + b[1]) / 2;
  const dx = (b[0] - a[0]) * metersPerDegLng(latMid);
  const dy = (b[1] - a[1]) * METERS_PER_DEG_LAT;
  return Math.hypot(dx, dy);
}

/**
 * 起点から「方位 bearing（北0°・時計回り）」に距離 dist(m) 進んだ点を返す。
 */
export function project(origin: LngLat, bearingDeg: number, dist: number): LngLat {
  const br = bearingDeg * DEG2RAD;
  const dNorth = dist * Math.cos(br);
  const dEast = dist * Math.sin(br);
  const lat = origin[1] + dNorth / METERS_PER_DEG_LAT;
  const lng = origin[0] + dEast / metersPerDegLng(origin[1]);
  return [lng, lat];
}

/** 点がポリゴン（リング）内にあるか。ray casting。 */
export function pointInRing(p: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  const x = p[0];
  const y = p[1];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** バウンディングボックス [minLng, minLat, maxLng, maxLat]。 */
export function bboxOf(ring: LngLat[]): [number, number, number, number] {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

/** ポリゴン（リング）の重心（頂点平均。三角分割はしない簡易版で十分）。 */
export function centroidOf(ring: LngLat[]): LngLat {
  let sx = 0;
  let sy = 0;
  for (const [x, y] of ring) {
    sx += x;
    sy += y;
  }
  return [sx / ring.length, sy / ring.length];
}

/** 指定緯度において、距離 meters(m) に相当する経度・緯度のオフセット量(度)。 */
export function degOffset(meters: number, lat: number): { dLng: number; dLat: number } {
  return { dLng: meters / metersPerDegLng(lat), dLat: meters / METERS_PER_DEG_LAT };
}

/** 中心点から半径 radiusM (m) の正方形バウンディングボックス。 */
export function squareBbox(
  center: LngLat,
  radiusM: number
): [number, number, number, number] {
  const { dLng, dLat } = degOffset(radiusM, center[1]);
  return [center[0] - dLng, center[1] - dLat, center[0] + dLng, center[1] + dLat];
}

/** 2直線(a-b, p1-p2)の交点。凸ポリゴンクリップ用の内部ヘルパー。 */
function lineIntersect(
  p1: [number, number],
  p2: [number, number],
  a: [number, number],
  b: [number, number]
): [number, number] {
  const d1x = p2[0] - p1[0];
  const d1y = p2[1] - p1[1];
  const d2x = b[0] - a[0];
  const d2y = b[1] - a[1];
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-9) return p2; // ほぼ平行(実用上発生しない)
  const t = ((a[0] - p1[0]) * d2y - (a[1] - p1[1]) * d2x) / denom;
  return [p1[0] + t * d1x, p1[1] + t * d1y];
}

/** 凸ポリゴンsubjectを、原点(0,0)を内側に含む直線a→bで切り取る(Sutherland-Hodgman)。 */
function clipByEdge(
  subject: [number, number][],
  a: [number, number],
  b: [number, number]
): [number, number][] {
  if (subject.length === 0) return subject;
  const ex = b[0] - a[0];
  const ey = b[1] - a[1];
  const side = (p: [number, number]) => ex * (p[1] - a[1]) - ey * (p[0] - a[0]);
  const insideSign = Math.sign(side([0, 0])) || 1;
  const inside = (p: [number, number]) => side(p) * insideSign >= 0;

  const output: [number, number][] = [];
  for (let i = 0; i < subject.length; i++) {
    const curr = subject[i];
    const prev = subject[(i + subject.length - 1) % subject.length];
    const currIn = inside(curr);
    const prevIn = inside(prev);
    if (currIn) {
      if (!prevIn) output.push(lineIntersect(prev, curr, a, b));
      output.push(curr);
    } else if (prevIn) {
      output.push(lineIntersect(prev, curr, a, b));
    }
  }
  return output;
}

/**
 * リング(凸ポリゴン)を、center中心・半径radiusM(m)の円でクリップする。
 * 円はsteps角形で近似し、Sutherland-Hodgmanで逐次クリップする(建物の影ポリゴンが
 * 肉眼での限界距離の外にはみ出さないようにする用途)。
 */
export function clipRingToCircle(
  ring: LngLat[],
  center: LngLat,
  radiusM: number,
  steps = 48
): LngLat[] {
  if (ring.length === 0) return ring;
  const mPerLng = metersPerDegLng(center[1]);
  const toXY = (p: LngLat): [number, number] => [
    (p[0] - center[0]) * mPerLng,
    (p[1] - center[1]) * METERS_PER_DEG_LAT,
  ];
  const fromXY = (p: [number, number]): LngLat => [
    center[0] + p[0] / mPerLng,
    center[1] + p[1] / METERS_PER_DEG_LAT,
  ];

  let subject = ring.map(toXY);
  for (let i = 0; i < steps && subject.length > 0; i++) {
    const a0 = (i / steps) * Math.PI * 2;
    const a1 = ((i + 1) / steps) * Math.PI * 2;
    const edgeA: [number, number] = [radiusM * Math.sin(a0), radiusM * Math.cos(a0)];
    const edgeB: [number, number] = [radiusM * Math.sin(a1), radiusM * Math.cos(a1)];
    subject = clipByEdge(subject, edgeA, edgeB);
  }
  return subject.map(fromXY);
}

/**
 * 折れ線(道路等)を、center中心・半径radiusM(m)の円でクリップする。
 * 円の内外を出入りする場合は複数の線分に分割して返す(円をまたいで完全に
 * 通過する区間=両端が外側の区間は簡略化のため無視する。道路のような細かく
 * 折れた線では実用上問題にならない)。
 */
export function clipLineToCircle(line: LngLat[], center: LngLat, radiusM: number): LngLat[][] {
  if (line.length < 2) return [];
  const mPerLng = metersPerDegLng(center[1]);
  const toXY = (p: LngLat): [number, number] => [
    (p[0] - center[0]) * mPerLng,
    (p[1] - center[1]) * METERS_PER_DEG_LAT,
  ];
  const fromXY = (p: [number, number]): LngLat => [
    center[0] + p[0] / mPerLng,
    center[1] + p[1] / METERS_PER_DEG_LAT,
  ];

  const r2 = radiusM * radiusM;
  const inside = (p: [number, number]) => p[0] * p[0] + p[1] * p[1] <= r2;

  /** insideの点aからoutsideの点bへ向かう線分と円の交点。 */
  const intersect = (a: [number, number], b: [number, number]): [number, number] => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const qa = dx * dx + dy * dy;
    const qb = 2 * (a[0] * dx + a[1] * dy);
    const qc = a[0] * a[0] + a[1] * a[1] - r2;
    if (qa === 0) return a;
    const disc = Math.max(qb * qb - 4 * qa * qc, 0);
    const t = (-qb + Math.sqrt(disc)) / (2 * qa);
    return [a[0] + t * dx, a[1] + t * dy];
  };

  const pts = line.map(toXY);
  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  if (inside(pts[0])) current.push(pts[0]);

  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const prevIn = inside(prev);
    const curIn = inside(cur);
    if (prevIn && curIn) {
      current.push(cur);
    } else if (prevIn && !curIn) {
      current.push(intersect(prev, cur));
      segments.push(current);
      current = [];
    } else if (!prevIn && curIn) {
      current = [intersect(cur, prev), cur];
    }
    // !prevIn && !curIn: 両方円の外。円をまたいで通過する稀なケースは無視する。
  }
  if (current.length >= 2) segments.push(current);

  return segments.map((seg) => seg.map(fromXY));
}

/** 凸包（Andrew's monotone chain）。入力・出力とも [lng, lat]。 */
export function convexHull(points: LngLat[]): LngLat[] {
  const pts = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length <= 2) return pts;
  const cross = (o: LngLat, a: LngLat, b: LngLat) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: LngLat[] = [];
  for (const p of pts) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    )
      lower.pop();
    lower.push(p);
  }
  const upper: LngLat[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    )
      upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}
